import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Services: React.FC = () => {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const services = [
    {
      id: 'guided-tours',
      title: 'Guided Fishing Tours',
      description: 'Expert-led fishing expeditions for all skill levels',
      longDescription: 'Experience the thrill of fishing at Loch Lomond with our expert guides. Our tours are tailored to your skill level, whether you\'re a beginner or an experienced angler. We provide all necessary equipment and ensure a safe, enjoyable experience.',
      price: 'From £150 per person',
      duration: '4-8 hours',
      includes: [
        'Professional guide',
        'All fishing equipment',
        'Safety gear',
        'Refreshments',
        'Fishing permit',
        'Transportation to fishing spots',
        'Photography service',
        'Fish cleaning and preparation'
      ],
      image: '/images/fishingboat1.jpg',
      icon: '🎣',
      highlights: [
        'Personalised instruction',
        'Best fishing spots',
        'Local knowledge',
        'Flexible scheduling'
      ]
    },
    {
      id: 'equipment-rental',
      title: 'Equipment Rental',
      description: 'High-quality fishing gear available for rent',
      longDescription: 'Access top-quality fishing equipment without the investment. Our rental service includes rods, reels, tackle, and safety gear. All equipment is regularly maintained and sanitized. Perfect for visitors who want to try fishing without committing to purchasing their own gear.',
      price: 'From £30 per day',
      duration: 'Daily or weekly',
      includes: [
        'Fishing rods and reels',
        'Tackle box with lures',
        'Fishing net',
        'Safety equipment',
        'Storage case',
        'Cleaning supplies',
        'Bait and hooks',
        'Fishing line'
      ],
      image: '/images/fishing2.jpg',
      icon: '🎒',
      highlights: [
        'Premium equipment',
        'Daily maintenance',
        'Expert advice',
        'Flexible rental periods'
      ]
    },
    {
      id: 'fishing-lessons',
      title: 'Fishing Lessons',
      description: 'Learn fishing techniques from professional anglers',
      longDescription: 'Master the art of fishing with our comprehensive lessons. Our experienced instructors will teach you everything from basic casting to advanced techniques. Perfect for beginners or those looking to improve their skills. We focus on both practical skills and theoretical knowledge.',
      price: 'From £75 per session',
      duration: '2-4 hours',
      includes: [
        'One-on-one instruction',
        'All necessary equipment',
        'Practice materials',
        'Written guide',
        'Certificate of completion',
        'Follow-up support',
        'Video recording of session',
        'Online resources'
      ],
      image: '/images/fishing3.jpg',
      icon: '📚',
      highlights: [
        'Personalised learning',
        'Hands-on practice',
        'Expert feedback',
        'Comprehensive curriculum'
      ]
    },
    {
      id: 'corporate-events',
      title: 'Corporate Events',
      description: 'Team building and corporate fishing events',
      longDescription: 'Create unforgettable team-building experiences with our corporate fishing events. Perfect for team bonding, client entertainment, or company retreats. We handle all the logistics and provide a unique outdoor experience that combines fun, learning, and team building.',
      price: 'Custom pricing',
      duration: 'Half or full day',
      includes: [
        'Customized program',
        'Professional guides',
        'All equipment',
        'Catering options',
        'Team activities',
        'Photography service',
        'Transportation',
        'Corporate branding options'
      ],
      image: '/images/fishingboat2.jpg',
      icon: '🤝',
      highlights: [
        'Team building',
        'Custom packages',
        'Professional service',
        'Flexible scheduling'
      ]
    },
    {
      id: 'specialty-tours',
      title: 'Specialty Fishing Tours',
      description: 'Specialised fishing experiences for enthusiasts',
      longDescription: 'Experience unique fishing adventures tailored to specific interests. From night fishing to fly fishing, our specialty tours offer something for every fishing enthusiast. Learn advanced techniques and discover hidden fishing spots around Loch Lomond.',
      price: 'From £200 per person',
      duration: '6-12 hours',
      includes: [
        'Specialised equipment',
        'Expert guide',
        'Transportation',
        'Meals and refreshments',
        'Photography service',
        'Fishing permit',
        'Safety equipment',
        'Specialised instruction'
      ],
      image: '/images/fishingboat3.jpg',
      icon: '🎯',
      highlights: [
        'Specialised techniques',
        'Unique locations',
        'Expert guidance',
        'Premium equipment'
      ]
    },
    {
      id: 'family-packages',
      title: 'Family Fishing Packages',
      description: 'Perfect fishing experiences for the whole family',
      longDescription: 'Create lasting memories with our family fishing packages. Designed for families of all sizes, these packages combine fun, education, and quality time together. Our guides ensure everyone has a great time, regardless of age or experience level.',
      price: 'From £250 per family',
      duration: '4-6 hours',
      includes: [
        'Family-sized equipment',
        'Child-friendly guides',
        'Safety equipment',
        'Snacks and drinks',
        'Family photos',
        'Fishing permits',
        'Educational materials',
        'Souvenir certificates'
      ],
      image: '/images/fishing111.jpg',
      icon: '👨‍👩‍👧‍👦',
      highlights: [
        'Family-friendly',
        'Educational fun',
        'Safe environment',
        'Memorable experience'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Our Services - Anchor Management</title>
        <meta name="description" content="Explore Anchor Management's comprehensive range of fishing services and packages at Loch Lomond" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/fishingboat1.jpg"
            alt="Fishing boat on Loch Lomond"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-4"
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-2xl mx-auto"
          >
            Discover our range of fishing experiences at Loch Lomond
          </motion.p>
        </div>
        
        {/* Wave Shape */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            className="w-full h-auto"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#00B4D8"
              fillOpacity="0.8"
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedService(service.id)}
              >
                <div className="relative h-48">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 text-4xl bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg">{service.icon}</div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">{service.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{service.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {service.highlights.map((highlight, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2 py-1 rounded-full"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-primary-600 dark:text-primary-400 font-medium">{service.price}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{service.duration}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service View */}
      {selectedService && (
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            {services.map((service) => (
              service.id === selectedService && (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden"
                >
                  <div className="relative h-96">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setSelectedService(null)}
                      className="absolute top-4 right-4 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Close service details"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-3xl font-bold">{service.title}</h2>
                      <span className="text-4xl bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg">{service.icon}</span>
                    </div>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">{service.longDescription}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                      <div>
                        <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">What's Included</h3>
                        <ul className="space-y-2">
                          {service.includes.map((item, index) => (
                            <li key={index} className="flex items-center text-gray-700 dark:text-gray-300">
                              <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">Highlights</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {service.highlights.map((highlight, index) => (
                            <div key={index} className="flex items-center text-gray-700 dark:text-gray-300">
                              <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {highlight}
                            </div>
                          ))}
                        </div>
                        <div className="mt-6 space-y-4">
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Duration</p>
                            <p className="text-lg font-medium text-gray-800 dark:text-white">{service.duration}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Price</p>
                            <p className="text-lg font-medium text-primary-600 dark:text-primary-400">{service.price}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <Link
                        to="/contact"
                        className="bg-primary-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-primary-700 transition-colors"
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>
                </motion.div>
              )
            ))}
          </div>
        </section>
      )}

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800 dark:text-white">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">Expert Guides</h3>
              <p className="text-gray-700 dark:text-gray-300">Our guides have years of experience and local knowledge</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">⭐</div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">Quality Equipment</h3>
              <p className="text-gray-700 dark:text-gray-300">Top-of-the-line gear maintained to the highest standards</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">🌿</div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">Sustainable Practices</h3>
              <p className="text-gray-700 dark:text-gray-300">Committed to preserving Loch Lomond's natural beauty</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services; 